import { useAppSelector } from '@store';
import {
  ApplicationSelector,
  ButtonPrimary,
  ButtonSecondary,
  formatDate,
  IconLoader,
  PermissionEnum,
  Popup,
  SidePanel,
  SidePanelType,
} from '@ui/src';
import useCompanyOfficial from './hooks/use-company-officials';
import StepPartBlock from '../common/step-part-block';
import CompanyOfficial from './parts/company-official';
import CreateForm from './parts/create-company-official';
import {
  AccessRight,
  ApplicationSchemeType,
  CofApprovalStatus,
  NewApplicationStatus,
} from '../../../hoc/common/types';
import { CompanyOfficialBlockTypes } from './types';

import commonStyles from '../common/styles.module.css';
import cn from './styles.module.css';

const CompanyOfficialBlock = ({
  isCof,
  applications,
  applicationId,
  fields,
  initialFields,
  onApplicationSubmit,
  questionnarieFields,
  defaultQuestionnarieFormValues,
  applicationScheme,
  onSaveAccountsCurrencies,
  onSaveAccountsFlows,
  onSaveExpectedFundsOrigins,
  applicationsList,
  setValue,
  setDefaultFormValues,
  defaultApplicationFormValues,
  zIndex,
  applicationStatus,
  getApplicationChat,
  isEditable,
  status,
  errors,
}: CompanyOfficialBlockTypes) => {
  const { applicationSchemes } = useAppSelector(
    (state) => state.customersDictionaries,
  );
  const { activePermissions } = useAppSelector((state) => state.root);

  const {
    list,
    setList,
    addErrorVisible,
    assignInProgress,
    autocompleteList,
    setPanelIsOpen,
    onSearch,
    autocompleteIsLoading,
    setAddErrorVisible,
    assignCompanyOfficial,
    searchQuery,
    setSearchQuery,
    getAutocompleteListAction,
    clearState,
    panelIsOpen,
    sidepanelError,
    isDirty,
    setDirtyPopupVisible,
    setSidepanelError,
    setSumSubError,
    reset,
    isLoading,
    onSubmit,
    createControl,
    createErrors,
    sumSubError,
    dirtyPopupVisible,
  } = useCompanyOfficial({
    isCof,
    applications,
    applicationId,
    fields,
    initialFields,
    onApplicationSubmit,
    questionnarieFields,
    defaultQuestionnarieFormValues,
    applicationScheme,
    onSaveAccountsCurrencies,
    onSaveAccountsFlows,
    onSaveExpectedFundsOrigins,
    applicationsList,
    setValue,
    setDefaultFormValues,
    defaultApplicationFormValues,
  });

  return (
    <div className={commonStyles.stepBlockWrapper} style={{ zIndex }}>
      <div className={commonStyles.stepBlockContent}>
        <StepPartBlock
          title={isCof ? 'Company official' : 'Main representative'}
        >
          <div className={commonStyles.accountsCurrenciesList}>
            {list.map((item, index) => (
              <CompanyOfficial
                key={item.id}
                value={item}
                setValue={setValue}
                setValuesList={setList}
                index={index}
                values={list}
                applicationId={applicationId}
                companyOfficial={applications?.find(
                  (user) => item.id === user.subApplicationId,
                )}
                applicationStatus={applicationStatus}
                initialFields={initialFields}
                fields={fields}
                onApplicationSubmit={onApplicationSubmit}
                questionnarieFields={questionnarieFields}
                defaultQuestionnarieFormValues={defaultQuestionnarieFormValues}
                applicationScheme={applicationScheme}
                getApplicationChat={getApplicationChat}
                disabled={
                  !isEditable ||
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  )
                }
                approveDisabled={Boolean(
                  applications?.find(
                    (user) => user.status === CofApprovalStatus.ACCEPTED,
                  ),
                )}
                auditLogAvailable={activePermissions?.includes(
                  PermissionEnum.AUDIT_LOG_LIST,
                )}
                type={isCof ? 'COF' : 'MR'}
                onSaveAccountsCurrencies={onSaveAccountsCurrencies}
                onSaveAccountsFlows={onSaveAccountsFlows}
                onSaveExpectedFundsOrigins={onSaveExpectedFundsOrigins}
              />
            ))}
            {addErrorVisible && (
              <div className={cn.sameUserError}>You can`t add same user.</div>
            )}
            {applicationStatus !== NewApplicationStatus.ACCEPTED &&
              applicationStatus !== NewApplicationStatus.DECLINED &&
              (activePermissions?.includes(
                PermissionEnum.INDIVIDUAL_CUSTOMERS_EDIT_DETAILS_ACTION,
              ) ||
                activePermissions?.includes(
                  PermissionEnum.CORPORATE_CUSTOMERS_EDIT_DETAILS_ACTION,
                )) && (
                <div className={cn.addNewCofWrapper}>
                  {!assignInProgress && (
                    <ApplicationSelector
                      title={
                        isCof
                          ? 'Add Company officials'
                          : 'Add Main representative'
                      }
                      placeholder="Select customer..."
                      onButtonClick={() => setPanelIsOpen(true)}
                      list={autocompleteList.map((item) => ({
                        ...item,
                        dob: item.dob ? formatDate(item.dob) : '-',
                      }))}
                      onSearch={onSearch}
                      isLoading={autocompleteIsLoading}
                      onApplicationClick={(id: string) => {
                        setAddErrorVisible(false);

                        if (
                          !applications?.find(
                            (item) => item.subApplication.id === id,
                          )
                        ) {
                          assignCompanyOfficial(id);
                        } else {
                          setAddErrorVisible(true);
                        }

                        if (searchQuery) {
                          setSearchQuery('');
                          getAutocompleteListAction('');
                        }
                      }}
                      clearState={clearState}
                      tabIndex={0}
                    />
                  )}
                  {assignInProgress && (
                    <div className={cn.assignLoaderWrapper}>
                      <IconLoader />
                      <span>{`${isCof ? 'Company Official' : 'Main Representative'} assign in progress...`}</span>
                    </div>
                  )}
                </div>
              )}
          </div>
        </StepPartBlock>

        <SidePanel
          type={SidePanelType.Customers}
          closeOnClickOutside
          isOpen={panelIsOpen}
          errorSidepanel={sidepanelError}
          sidepanelExpandedKey="application-cof-create"
          onClose={() => {
            if (isDirty) {
              setDirtyPopupVisible(true);
            } else {
              setSidepanelError('');
              setSumSubError('');
              setPanelIsOpen(false);
              reset({
                accessRight: AccessRight.VIEWER,
                applicantId: '',
                deliveryChannelId: '',
                dob: '',
                email: '',
                firstName: '',
                isUSResident: false,
                lastName: '',
                nationalityId: '',
                participatesInPolitics: false,
                phone: '',
                signatureType: '',
                applicationSchemeId: '',
              });
            }
          }}
          title={`Create ${isCof ? 'Company Official' : 'Main representative'} application`}
          footer={
            <div style={{ display: 'flex' }}>
              <ButtonPrimary
                text={
                  isLoading || assignInProgress
                    ? 'Loading...'
                    : 'Create application'
                }
                onClick={onSubmit}
                bold
                disabled={isLoading || assignInProgress}
                tabIndex={-1}
              />
              <ButtonSecondary
                text="Cancel"
                onClick={() => {
                  if (isDirty) {
                    setDirtyPopupVisible(true);
                  } else {
                    setSidepanelError('');
                    setSumSubError('');
                    setPanelIsOpen(false);
                    reset({
                      accessRight: AccessRight.VIEWER,
                      applicantId: '',
                      deliveryChannelId: '',
                      dob: '',
                      email: '',
                      firstName: '',
                      isUSResident: false,
                      lastName: '',
                      nationalityId: '',
                      participatesInPolitics: false,
                      phone: '',
                      signatureType: '',
                      applicationSchemeId: '',
                    });
                  }
                }}
                tabIndex={-1}
                bold
              />
              {errors && Object.keys(errors)?.length > 0 && (
                <span className={cn.formError}>
                  There are errors in some fields
                </span>
              )}
            </div>
          }
        >
          <CreateForm
            control={createControl}
            errors={createErrors}
            sumSubError={sumSubError}
            applicationSchemes={applicationSchemes?.filter(
              (item) => item.type === ApplicationSchemeType.COMPANY_OFFICIAL,
            )}
          />
        </SidePanel>
        <Popup
          title="You have unsaved changes that will be lost"
          description="Are you sure you want to close this form?"
          onCancelClick={() => setDirtyPopupVisible(false)}
          onOkClick={() => {
            setPanelIsOpen(false);
            setDirtyPopupVisible(false);
            setSidepanelError('');
            setSumSubError('');
            reset({
              accessRight: AccessRight.VIEWER,
              applicantId: '',
              deliveryChannelId: '',
              dob: '',
              email: '',
              firstName: '',
              isUSResident: false,
              lastName: '',
              nationalityId: '',
              participatesInPolitics: false,
              phone: '',
              signatureType: '',
              applicationSchemeId: '',
            });
          }}
          cancelButtonText="Cancel"
          okButtonText="Close"
          isVisible={dirtyPopupVisible}
          parent={document.getElementById('dirty-popup') as HTMLElement}
        />
        <div id="dirty-popup" />
      </div>
    </div>
  );
};

export default CompanyOfficialBlock;
